import React, { useState, useCallback } from "react";
import { ArrowLeft, Eye, EyeOff, KeyRound, Loader2, Lock, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { EMAIL_FORMAT_REGEX, EMPTY_STRING, INVALID_EMAIL_FORMAT_ERROR, LOGIN_PAGE, NIMIS_WEBSITE_URL } from "@/constants/definitions";
import PasswordValidationClass from "@/utils/passwordValidation";
import { Location } from "react-router-dom";

type NavState = {
  from?: string;
  emailAddress: string;
};

interface ResetPasswordProps {
  getUserDetails: () => Promise<void>;
  cognitologin: { resetPassword: (username: string, code: string, password: string) => Promise<string> };
  navigate: (path: string) => void;
  location: Location & { state?: NavState };
}

export default function ResetPassword({ cognitologin, navigate, location }: ResetPasswordProps) {
  const navState = (location?.state ?? {}) as NavState;

  const [email, setEmail] = useState(navState.emailAddress || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateOnBlurNewPassword = useCallback(() => {
    if (!newPassword) return;
    const validator = new PasswordValidationClass();
    const firstError = validator.validateFirstPassword(newPassword, "", true);
    setValidationError(firstError);
  }, [newPassword]);

  const validateOnBlurConfirmPassword = useCallback(() => {
    if (!confirmPassword) return;
    const validator = new PasswordValidationClass();
    const firstError = validator.validateFirstPassword(newPassword, "", true);
    if (firstError) {
      setValidationError(firstError);
      return;
    }
    const secondError = validator.validateSecondPassword(newPassword, confirmPassword, firstError);
    setValidationError(secondError);
  }, [newPassword, confirmPassword]);

  const hasNoValidationErrors = !validationError && newPassword === confirmPassword && newPassword.length > 0;
  const isFormValid = email && verificationCode && newPassword && confirmPassword && hasNoValidationErrors;

  const handleSubmit = useCallback(async () => {
    if (!EMAIL_FORMAT_REGEX.test(email)) {
      setErrorMessage(INVALID_EMAIL_FORMAT_ERROR);
      return;
    }

    const validator = new PasswordValidationClass();
    const firstError = validator.validateFirstPassword(newPassword, "", true);
    const finalError = validator.validateSecondPassword(newPassword, confirmPassword, firstError);

    if (finalError) {
      setValidationError(finalError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");
      const result = await cognitologin.resetPassword(email, verificationCode, newPassword);

      if (result === EMPTY_STRING) {
        navigate(LOGIN_PAGE);
      } else {
        setErrorMessage(result);
      }
    } catch (resetError) {
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [email, verificationCode, newPassword, confirmPassword, cognitologin, navigate]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") handleSubmit();
    },
    [handleSubmit]
  );

  const displayError = errorMessage || validationError;

  return (
    <div data-theme="dark" className="relative" style={{ background: "hsla(222, 70%, 8%, 0.85)", minHeight: "calc(100vh + 1px)" }}>
      <div className="pointer-events-none absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59,130,246,0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14,165,233,0.05) 0%, transparent 70%)",
      }} />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6">
        <a href={NIMIS_WEBSITE_URL}>
          <img src="/nimis-intelligence-white-logo.png" alt="NIMIS Intelligence" className="h-12 w-auto" />
        </a>
      </div>

      <div className="relative z-10 flex justify-center pt-[8vh]">
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-white">Reset your passphrase</h1>
              <p className="mt-1.5 text-sm text-white/50">
                Enter the verification code sent to your email
              </p>
            </div>

            {displayError && (
              <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                {displayError}
              </div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <FormField label="Email" htmlFor="email" variant="dark">
                <Input
                  id="email"
                  type="email"
                  variant="dark"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(changeEvent) => { setEmail(changeEvent.target.value); setErrorMessage(""); }}
                  onKeyDown={handleKeyDown}
                  autoComplete="email"
                  disabled={isLoading || !!navState.emailAddress}
                  readOnly={!!navState.emailAddress}
                  leftIcon={<Mail className="h-4 w-4" />}
                />
              </FormField>

              {/* Verification Code */}
              <FormField label="Verification Code" htmlFor="verificationCode" variant="dark">
                <Input
                  id="verificationCode"
                  type="text"
                  variant="dark"
                  placeholder="Enter code from email"
                  value={verificationCode}
                  onChange={(changeEvent) => { setVerificationCode(changeEvent.target.value); setErrorMessage(""); }}
                  onKeyDown={handleKeyDown}
                  autoComplete="one-time-code"
                  disabled={isLoading}
                  leftIcon={<KeyRound className="h-4 w-4" />}
                />
              </FormField>

              {/* New Password */}
              <FormField
                label="New Passphrase"
                htmlFor="newPassword"
                variant="dark"
                helperText="Min 12 characters with uppercase, lowercase, number and symbol"
              >
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  variant="dark"
                  placeholder="Enter new passphrase"
                  value={newPassword}
                  onChange={(changeEvent) => { setNewPassword(changeEvent.target.value); setErrorMessage(""); setValidationError(""); }}
                  onBlur={validateOnBlurNewPassword}
                  onKeyDown={handleKeyDown}
                  autoComplete="new-password"
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-white/30 hover:text-white/60 transition-colors"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
              </FormField>

              {/* Confirm Password */}
              <FormField label="Confirm Passphrase" htmlFor="confirmPassword" variant="dark">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  variant="dark"
                  placeholder="Confirm new passphrase"
                  value={confirmPassword}
                  onChange={(changeEvent) => { setConfirmPassword(changeEvent.target.value); setErrorMessage(""); setValidationError(""); }}
                  onBlur={validateOnBlurConfirmPassword}
                  onKeyDown={handleKeyDown}
                  autoComplete="new-password"
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-white/30 hover:text-white/60 transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
              </FormField>

              <Button
                onClick={handleSubmit}
                disabled={isLoading || !isFormValid}
                className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors disabled:bg-blue-600/50"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting passphrase...</>
                ) : (
                  "Reset passphrase"
                )}
              </Button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate(LOGIN_PAGE)}
                className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
